/**
 * Real PDF text extractor using browser-based text extraction
 */

export async function extractTextFromPDF(file) {
  try {
    console.log("📄 Real PDF extraction: Starting text extraction for:", file.name);
    console.log("📄 File size:", file.size, "bytes");
    console.log("📄 File type:", file.type);
    
    // Check if PDF.js is available
    if (!window.pdfjsLib) {
      throw new Error("PDF.js library not available - cannot extract text from PDF. Please refresh the page and try again.");
    }
    
    console.log("📄 PDF.js library is available, proceeding with extraction...");
    
    // Extract real text from the PDF
    try {
      const realText = await extractRealPDFText(file);
      // Lower threshold for text extraction (previously 50, now 10)
      if (realText && realText.trim().length > 10) {
        console.log("✅ Real PDF extraction successful! Text length:", realText.length);
        console.log("📄 Sample extracted text:", realText.substring(0, 300) + "...");
        return realText;
      } else {
        console.log("⚠️ Real PDF extraction returned insufficient text:", realText?.length || 0, "characters");
        
        // Try basic text extraction as fallback
        console.log("📄 Trying basic text extraction as fallback...");
        try {
          const basicText = await basicTextExtraction(file);
          if (basicText && basicText.trim().length > 10) {
            console.log("✅ Basic text extraction successful! Text length:", basicText.length);
            return basicText;
          }
        } catch (basicError) {
          console.log("⚠️ Basic text extraction also failed:", basicError);
        }
        
        throw new Error(`Insufficient text extracted from PDF. This PDF may be:
- A scanned document (image-based) that requires OCR
- Password protected
- Corrupted or malformed 
- Contains only images/graphics without searchable text

Having trouble? Convert your scanned document to searchable text:
👉 Click here: https://tools.pdf24.org/en/ocr-pdf

Other solutions:
1. Use a different PDF file with searchable text
2. Check if the PDF opens correctly in a PDF reader and contains selectable text`);
      }
    } catch (extractionError) {
      console.error("❌ Real PDF extraction failed:", extractionError);
      throw extractionError;
    }
    
  } catch (error) {
    console.error('Error in PDF extraction:', error);
    throw new Error('Failed to extract text from PDF file');
  }
}


// Real PDF text extraction function
async function extractRealPDFText(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    
    fileReader.onload = async function() {
      try {
        const typedArray = new Uint8Array(this.result);
        
        // Try to use PDF.js if available (modern browsers)
        if (window.pdfjsLib) {
          console.log("📄 Using PDF.js for text extraction");
          try {
            const pdf = await window.pdfjsLib.getDocument(typedArray).promise;
            let fullText = '';
            
            console.log(`📄 PDF has ${pdf.numPages} pages`);
            
            for (let i = 1; i <= pdf.numPages; i++) {
              try {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                
                // More robust text extraction with better handling
                const pageText = textContent.items
                  .map(item => {
                    // Handle different text item formats
                    if (typeof item === 'string') return item;
                    if (item && item.str) return item.str;
                    if (item && item.text) return item.text;
                    return '';
                  })
                  .filter(str => str && str.trim().length > 0) // Remove empty/null strings
                  .join(' ');
                
                console.log(`📄 Page ${i} extracted ${pageText.length} characters`);
                console.log(`📄 Page ${i} sample text:`, pageText.substring(0, 100));
                
                if (pageText.trim().length > 0) {
                  fullText += pageText + '\n';
                }
              } catch (pageError) {
                console.warn(`⚠️ Failed to extract text from page ${i}:`, pageError);
              }
            }
            
            console.log(`📄 Total extracted text length: ${fullText.length}`);
            if (fullText.trim().length > 0) {
              resolve(fullText);
            } else {
              throw new Error(`No text extracted from PDF pages. This PDF may be:
- A scanned document (image-based) that requires OCR
- Password protected
- Corrupted or malformed 
- Contains only images/graphics without searchable text

Having trouble? Convert your scanned document to searchable text:
👉 Click here: https://tools.pdf24.org/en/ocr-pdf

Other solutions:
1. Use a different PDF file with searchable text
2. Check if the PDF opens correctly in a PDF reader`);
            }
          } catch (pdfError) {
            console.error("❌ PDF.js extraction failed:", pdfError);
            throw pdfError;
          }
        } else {
          // Fallback: Try to extract text using basic parsing
          console.log("📄 PDF.js not available, trying basic text extraction");
          const text = performBasicTextExtraction(typedArray);
          resolve(text);
        }
      } catch (error) {
        console.error("❌ PDF text extraction failed:", error);
        reject(error);
      }
    };
    
    fileReader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    fileReader.readAsArrayBuffer(file);
  });
}

// Basic text extraction as fallback that accepts a file
async function basicTextExtraction(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    
    fileReader.onload = function() {
      try {
        const typedArray = new Uint8Array(this.result);
        const result = performBasicTextExtraction(typedArray);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    
    fileReader.onerror = () => {
      reject(new Error('Failed to read file for basic extraction'));
    };
    
    fileReader.readAsArrayBuffer(file);
  });
}

// Helper function for basic text extraction from typed array
function performBasicTextExtraction(typedArray) {
  // Convert to string and try to find readable text
  const decoder = new TextDecoder('utf-8');
  const text = decoder.decode(typedArray);
  
  // Look for readable text patterns in the PDF
  const textMatches = text.match(/[A-Za-z\s]{10,}/g);
  if (textMatches && textMatches.length > 0) {
    // Join and clean up the extracted text
    const extractedText = textMatches.join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    if (extractedText.length > 100) {
      return extractedText;
    }
  }
  
  throw new Error('No readable text found in PDF');
}

export function isPDFFile(file) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

// Helper function to generate random names for demo data
function getRandomName() {
  const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Lisa', 'Robert', 'Mary', 'James', 'Jennifer'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
  return `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
}
